/**
 * THE PALETTE — nine steps, each with a sentence saying what it does.
 *
 * A palette of nine icons is a memory test. Every item here carries one line in an ISP
 * owner's words, because the person building their first flow at 11 PM has never seen this
 * screen before and "branch" means nothing on its own.
 *
 * IT IS NOT DRAG-ONLY. Each item is a real <button>: click or press Enter and the step is
 * added, wired onto whatever is selected if that has a free outlet. Drag is the nice way; the
 * button is the way that works with a keyboard, a trackpad somebody finds hard, or a screen
 * reader. A canvas whose only route to adding a step is a mouse gesture is a canvas half the
 * people who need it cannot use — and it is also the canvas that breaks on a touch screen.
 */
import type { ReactNode } from 'react';

import { Icon } from '../Icon.tsx';
import { HelpTip } from '../HelpTip.tsx';
import { NODE_SPECS, PALETTE_ORDER } from './model.ts';
import type { NdKind } from './model.ts';

export const DRAG_MIME = 'application/x-nightdesk-node';

export type PaletteProps = {
  onAdd: (kind: NdKind) => void;
  disabled: boolean;
  /** Where a keyboard-added step will land, said in words so it is not a surprise. */
  attachHint: string | null;
};

export function Palette({ onAdd, disabled, attachHint }: PaletteProps): ReactNode {
  return (
    <div className="ndf-palette" aria-label="Steps you can add">
      <div className="ndf-palette-head">
        <span className="nd-eyebrow">
          Add a step <HelpTip helpKey="flows.palette" />
        </span>
        <p className="ndf-palette-hint">
          {disabled
            ? 'Your role can read this flow but not change it.'
            : attachHint
              ? `Drag onto the canvas, or press one to add it ${attachHint}.`
              : 'Drag one onto the canvas — drop it on a line to slot it in between two steps.'}
        </p>
      </div>

      <ul className="ndf-palette-list">
        {PALETTE_ORDER.map((kind) => (
          <PaletteItem key={kind} kind={kind} onAdd={onAdd} disabled={disabled} />
        ))}
      </ul>
    </div>
  );
}

function PaletteItem({
  kind,
  onAdd,
  disabled,
}: {
  kind: NdKind;
  onAdd: (kind: NdKind) => void;
  disabled: boolean;
}): ReactNode {
  const spec = NODE_SPECS[kind];
  return (
    <li>
      <button
        type="button"
        className={`ndf-palette-item ndf-palette-item--${spec.tone}`}
        draggable={!disabled}
        disabled={disabled}
        onDragStart={(event) => {
          event.dataTransfer.setData(DRAG_MIME, kind);
          // A plain-text copy so a drop handler that only understands text still gets it,
          // and so dragging out of the window does not paste an empty string somewhere.
          event.dataTransfer.setData('text/plain', spec.label);
          event.dataTransfer.effectAllowed = 'copy';
        }}
        onClick={() => onAdd(kind)}
      >
        <span className="ndf-palette-chip" aria-hidden="true">
          <Icon name={spec.icon} size={14} />
        </span>
        <span className="ndf-palette-text">
          <span className="ndf-palette-label">{spec.label}</span>
          <span className="ndf-palette-blurb">{spec.blurb}</span>
        </span>
      </button>
    </li>
  );
}

export default Palette;
